import type { Tax } from '@monorepo/shared/schemas/taxes'
import { DollarSign, GripVertical, Percent, Search, X } from 'lucide-react'
import { useState } from 'react'
import { useTaxesList } from '@/features/taxes/hooks/useTaxes'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'

export interface SelectedTax {
  tax_id: string
  priority: number
}

export interface TaxSelectorProps {
  selectedTaxIds?: string[]
  onSelectionChange: (selection: {
    tax_ids: string[]
    replace_existing: boolean
  }) => void
  replaceExisting?: boolean
}

export function TaxSelector({
  selectedTaxIds = [],
  onSelectionChange,
  replaceExisting = false,
}: TaxSelectorProps) {
  const { taxes, isLoading } = useTaxesList({ is_active: true })
  const [selected, setSelected] = useState<string[]>(selectedTaxIds)
  const [replace, setReplace] = useState(replaceExisting)
  const [searchTerm, setSearchTerm] = useState('')
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // Filter taxes based on search
  const filteredTaxes = taxes.filter(
    tax =>
      tax.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tax.code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Get selected taxes in order
  const selectedTaxes = selected
    .map(id => taxes.find(t => t.id === id))
    .filter((t): t is Tax => t !== undefined)

  // Handle checkbox toggle
  const handleToggle = (taxId: string) => {
    const newSelected = selected.includes(taxId)
      ? selected.filter(id => id !== taxId)
      : [...selected, taxId]

    setSelected(newSelected)
    onSelectionChange({ tax_ids: newSelected, replace_existing: replace })
  }

  // Handle remove from selected
  const handleRemove = (taxId: string) => {
    const newSelected = selected.filter(id => id !== taxId)
    setSelected(newSelected)
    onSelectionChange({ tax_ids: newSelected, replace_existing: replace })
  }

  // Handle replace existing toggle
  const handleReplaceToggle = (checked: boolean) => {
    setReplace(checked)
    onSelectionChange({ tax_ids: selected, replace_existing: checked })
  }

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newSelected = [...selected]
    const draggedItem = newSelected[draggedIndex]
    newSelected.splice(draggedIndex, 1)
    newSelected.splice(index, 0, draggedItem)

    setSelected(newSelected)
    setDraggedIndex(index)
    onSelectionChange({ tax_ids: newSelected, replace_existing: replace })
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Format tax display
  const formatTaxRate = (tax: Tax) => {
    if (tax.tax_type === 'percentage' && tax.percentage_value) {
      return `${tax.percentage_value}%`
    }
    if (tax.tax_type === 'fixed' && tax.fixed_amount_cents) {
      return `$${(tax.fixed_amount_cents / 100).toFixed(2)}`
    }
    return ''
  }

  const getTaxIcon = (tax: Tax) => {
    return tax.tax_type === 'percentage' ? Percent : DollarSign
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8 text-sm text-muted-foreground'>
        Loading taxes...
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {/* Search */}
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          placeholder='Search taxes by name or code...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className='pl-9'
        />
      </div>

      {/* Replace existing option */}
      <div className='flex items-center justify-between rounded-lg border p-3'>
        <div className='space-y-0.5'>
          <Label htmlFor='replace-existing' className='text-sm font-medium'>
            Replace existing taxes
          </Label>
          <p className='text-xs text-muted-foreground'>
            Remove all current tax assignments before adding selected taxes
          </p>
        </div>
        <Switch
          id='replace-existing'
          checked={replace}
          onCheckedChange={handleReplaceToggle}
        />
      </div>

      {/* Selected taxes (draggable list) */}
      {selectedTaxes.length > 0 && (
        <div className='space-y-2'>
          <Label className='text-sm font-medium'>
            Selected Taxes ({selectedTaxes.length})
          </Label>
          <p className='text-xs text-muted-foreground'>
            Drag to reorder priority (top = highest priority)
          </p>
          <div className='space-y-2 rounded-lg border p-3'>
            {selectedTaxes.map((tax, index) => {
              const Icon = getTaxIcon(tax)
              return (
                // biome-ignore lint/a11y/useSemanticElements: draggable div
                <div
                  key={tax.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={e => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  role='listitem'
                  className={`flex items-center gap-3 rounded-md border bg-background p-3 transition-colors ${
                    draggedIndex === index
                      ? 'opacity-50'
                      : 'hover:bg-accent cursor-move'
                  }`}
                >
                  <GripVertical className='h-4 w-4 text-muted-foreground' />
                  <Badge variant='outline' className='shrink-0'>
                    #{index + 1}
                  </Badge>
                  <Icon className='h-4 w-4 text-muted-foreground' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate'>{tax.name}</p>
                    <p className='text-xs text-muted-foreground'>{tax.code}</p>
                  </div>
                  <Badge variant='secondary'>{formatTaxRate(tax)}</Badge>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => handleRemove(tax.id)}
                    className='h-8 w-8 p-0'
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Available taxes */}
      <div className='space-y-2'>
        <Label className='text-sm font-medium'>
          Available Taxes ({filteredTaxes.length})
        </Label>
        <div className='max-h-[300px] space-y-2 overflow-y-auto rounded-lg border p-3'>
          {filteredTaxes.length === 0 ? (
            <p className='text-center text-sm text-muted-foreground py-8'>
              {searchTerm
                ? 'No taxes found matching your search'
                : 'No taxes available'}
            </p>
          ) : (
            filteredTaxes.map(tax => {
              const isSelected = selected.includes(tax.id)
              const Icon = getTaxIcon(tax)

              return (
                <div
                  key={tax.id}
                  className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                    isSelected ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                >
                  <Checkbox
                    id={`tax-${tax.id}`}
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(tax.id)}
                  />
                  <Icon className='h-4 w-4 text-muted-foreground' />
                  <div className='flex-1 min-w-0'>
                    <Label
                      htmlFor={`tax-${tax.id}`}
                      className='text-sm font-medium cursor-pointer truncate block'
                    >
                      {tax.name}
                    </Label>
                    <p className='text-xs text-muted-foreground'>{tax.code}</p>
                  </div>
                  <Badge variant='secondary'>{formatTaxRate(tax)}</Badge>
                  {tax.jurisdiction && (
                    <Badge variant='outline' className='text-xs'>
                      {tax.jurisdiction}
                    </Badge>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
